import styled from "styled-components";
import { useTranslation } from "react-i18next";
import { SidePanel, Button } from "@xfrontend/xui-react-plus";
import { UserItem } from "#/apis/infra/users";

const Block = styled.div`margin-bottom: 18px;`;
const Label = styled.div`
  font-size: 10.5px; letter-spacing: .05em; text-transform: uppercase;
  color: var(--x-gray-500, #a9a9ac); margin-bottom: 4px;
`;
const Value = styled.div`font-size: 14px; color: var(--x-gray-200, #f6f6f7); word-break: break-all;`;

const StatusVal = styled.div<{ $on: boolean }>`
  display: inline-flex; align-items: center; gap: 7px; font-size: 14px; font-weight: 600;
  color: ${(p: { $on: boolean }) => (p.$on ? "var(--x-theme-success, #14B8A6)" : "var(--x-gray-500, #a9a9ac)")};
  .d { width: 8px; height: 8px; border-radius: 50%; background: currentColor; }
`;

const RoleRow = styled.div`
  display: flex; align-items: center; gap: 8px; padding: 9px 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
  &:last-child { border-bottom: none; }
`;
const RoleTag = styled.span`
  font-size: 10.5px; font-weight: 700; color: var(--x-gray-300, #e9e9eb);
  background: var(--x-purple-500, #494358); border-radius: 20px; padding: 3px 8px;
`;
const ScopeText = styled.span`color: var(--x-gray-400, #cfd0d3); font-size: 13px;`;
const Empty = styled.div`color: var(--x-gray-500, #a9a9ac); font-size: 13px;`;

// User status enum (xecguardcloud users_service): "active" = enabled, "disable" = disabled.
const isEnabled = (status: string): boolean => status !== "disable";

type UserDetailPanelProps = {
  user: UserItem | null;
  canManage: boolean;
  scopeName: (scopeType: string, scopeId: string) => string;
  onManageRoles: (user: UserItem) => void;
  onClose: () => void;
};

// Slide-out user detail (xui SidePanel = xcockpit's row-detail Offcanvas). Opened on row dblclick.
const UserDetailPanel = ({
  user, canManage, scopeName, onManageRoles, onClose,
}: UserDetailPanelProps): JSX.Element => {
  const { t } = useTranslation("management");
  const on = !!user && isEnabled(user.status);

  return (
    <SidePanel
      show={!!user}
      onClose={onClose}
      placement="end"
      title={user?.username ?? t("detail.title")}
      withCloseButton
    >
      {user && (
        <div>
          <Block>
            <Label>{t("form.email")}</Label>
            <Value>{user.email}</Value>
          </Block>
          <Block>
            <Label>{t("users.colStatus")}</Label>
            <StatusVal $on={on}>
              <i className="d" />
              {on ? t("status.enabled") : t("status.disabled")}
            </StatusVal>
          </Block>
          <Block>
            <Label>{t("detail.userId")}</Label>
            <Value>{user.user_id}</Value>
          </Block>
          <Block>
            <Label>{t("detail.roles")}</Label>
            {user.assignments.length === 0 && <Empty>{t("users.noRole")}</Empty>}
            {user.assignments.map((a) => (
              <RoleRow key={`${a.role}:${a.scope_type}:${a.scope_id}`}>
                <RoleTag>{a.role}</RoleTag>
                <ScopeText>{`@ ${scopeName(a.scope_type, a.scope_id)}`}</ScopeText>
              </RoleRow>
            ))}
          </Block>
          {canManage && (
            <Button
              variant="primary"
              onClick={() => { onManageRoles(user); onClose(); }}
            >
              {t("users.manageRoles")}
            </Button>
          )}
        </div>
      )}
    </SidePanel>
  );
};

export default UserDetailPanel;
